import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Alert,
  Link as MuiLink,
  Paper,
  Divider,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// MUI Icons
import SecurityIcon from "@mui/icons-material/Security";
import LockIcon from "@mui/icons-material/Lock";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedIcon from "@mui/icons-material/Verified";
import HandshakeIcon from "@mui/icons-material/Handshake";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import MemoryIcon from "@mui/icons-material/Memory";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CodeIcon from "@mui/icons-material/Code";
import ScaleIcon from "@mui/icons-material/Scale";
import StorageIcon from "@mui/icons-material/Storage";
import MapIcon from "@mui/icons-material/Map";
import SearchIcon from "@mui/icons-material/Search";
import InfoBox from "./utils/InfoBox";
import SmartToyIcon from "@mui/icons-material/SmartToy";

// Import common components
import CustomAccordion from "../common/CustomAccordion";
import EmbededContactForm from "./EmbededContactForm";
import SetDocumentMeta from "../common/SetDocumentMeta";

// Feature card component for displaying key features or services
const FeatureCard = ({ title, icon, content, color = "#4ABDAC" }) => {
  return (
    <Card
      elevation={1}
      sx={{
        height: "100%",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Box sx={{ color, mr: 1.5 }}>{icon}</Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color, fontWeight: "medium" }}
          >
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2">{content}</Typography>
      </CardContent>
    </Card>
  );
};

// Define "Jump to Section" items with icons
const jumpToSections = [
  {
    id: "ts-definition",
    label: "What are Trade Secrets?",
    icon: <InfoIcon sx={{ mr: 1 }} />,
  },
  {
    id: "ai-trade-secrets",
    label: "Trade Secrets in AI",
    icon: <MemoryIcon sx={{ mr: 1 }} />,
  },
  {
    id: "regional-considerations",
    label: "Arizona & California Laws",
    icon: <MapIcon sx={{ mr: 1 }} />,
  },
  {
    id: "our-services",
    label: "Our Trade Secret Services",
    icon: <SecurityIcon sx={{ mr: 1 }} />,
  },
  {
    id: "why-choose-us",
    label: "Why Choose Us",
    icon: <CheckCircleOutlineIcon sx={{ mr: 1 }} />,
  },
  {
    id: "contact-section",
    label: "Schedule a Free Consultation",
    icon: <PhoneInTalkIcon sx={{ mr: 1 }} />,
  },
];

const KthTradeSecrets = ({ baseConfig }) => {
  const {
    priColor,
    priHoverColor,
    secColor,
    tertColor,
    tertHoverColor,
    quadColor,
    sextColor,
    linkColor,
  } = baseConfig.siteBranding;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // SEO metadata
  SetDocumentMeta({
    title: "Trade Secret Protection for Technology & AI Companies",
    description:
      "Expert trade secret protection for technology and AI innovations. We provide comprehensive strategies to protect your AI algorithms, datasets, and proprietary technical information in Arizona and California.",
    keywords:
      "trade secret attorney, \
      AI trade secrets, \
      technology trade secrets, \
      NDA agreements, \
      non-compete agreements, \
      Arizona trade secret law, \
      California trade secret law, \
      AI algorithm protection, \
      confidential information, \
      proprietary technology, \
      data protection, \
      trade secret strategy, \
      intellectual property, \
      training data protection, \
      AI confidentiality",
  });

  // Custom styles
  const sectionStyles = {
    header: {
      py: 3,
      px: isMobile ? 2 : 4,
      mb: 4,
      borderRadius: 2,
      backgroundImage: `linear-gradient(135deg, ${priColor}22 0%, ${priColor}44 100%)`,
      boxShadow: "0 3px 5px rgba(0, 0, 0, 0.1)",
    },
    sectionTitle: {
      px: 2,
      py: 1.5,
      mb: 2,
      borderRadius: 1,
      backgroundColor: priColor,
      color: "white",
      fontWeight: "medium",
      display: "flex",
      alignItems: "center",
    },
    card: {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      borderRadius: 2,
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        transform: "translateY(-2px)",
      },
    },
    accordionTitle: {
      fontWeight: "medium",
      color: "text.primary",
    },
  };

  // Function to scroll to a specific section
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Container sx={{ py: 4 }} className="link-no-decoration">
      {/* ======== PAGE HEADER ======== */}
      <Box component="header" sx={sectionStyles.header}>
        {/* Banner / Headline */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            color="text.primary"
          >
            Trade Secret Protection for Technology & AI
          </Typography>
          <Typography
            variant="h6"
            fontWeight="medium"
            color="text.secondary"
            gutterBottom
          >
            Safeguarding Your Most Valuable Technical Assets
          </Typography>
          <Divider
            sx={{ my: 2, width: "20%", mx: "auto", borderColor: priColor }}
          />
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            lineHeight: 1.8,
            textAlign: "center",
            maxWidth: "85%",
            mx: "auto",
            fontSize: "1.1rem",
          }}
        >
          <strong>In today's competitive technology landscape,</strong> your
          proprietary algorithms, technical know-how, and unique methodologies
          are critical business assets. Our firm provides specialized trade
          secret protection strategies for engineering companies and AI
          innovators, helping you identify, secure, and enforce protection for
          your most valuable technical information.
        </Typography>
      </Box>

      {/* Banner for scheduling */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: linkColor,
          p: 2,
          borderRadius: 2,
          mt: 2,
          mb: 2,
          cursor: "pointer",
        }}
        onClick={() => {
          const ctaSection = document.getElementById("contact-section");
          if (ctaSection) {
            ctaSection.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        <PhoneInTalkIcon sx={{ mr: 1, color: "white" }} />
        <Typography variant="body1" sx={{ color: "white" }}>
          <strong>Schedule Your Free Trade Secret Strategy Consultation</strong>{" "}
          to protect your technology innovations!
        </Typography>
      </Box>

      {/* ======== TABLE OF CONTENTS ======== */}
      <Card variant="outlined" sx={{ ...sectionStyles.card, mb: 4 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="medium"
            sx={{
              color: priColor,
              borderBottom: `2px solid ${priColor}`,
              pb: 1,
            }}
          >
            Quick Navigation
          </Typography>

          <List dense disablePadding>
            {jumpToSections.map((section) => (
              <ListItem key={section.id} disablePadding>
                <ListItemButton
                  onClick={() => scrollToSection(section.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: `${priColor}22`,
                      pl: 2,
                    },
                  }}
                >
                  <Box sx={{ color: priColor }}>{section.icon}</Box>
                  <ListItemText primary={section.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* ======== MAIN CONTENT SECTIONS ======== */}

      {/* 1. What are Trade Secrets? */}
      <Box id="ts-definition" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${priColor}22`,
            }}
          >
            <InfoIcon sx={{ mr: 2, color: tertColor }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: tertColor }}
            >
              What Are Trade Secrets?
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              A trade secret is confidential, commercially valuable information
              that provides a business with a competitive advantage and is
              subject to reasonable measures to keep it secret. Unlike patents,
              trade secrets have no formal registration system and can
              potentially last indefinitely as long as secrecy is maintained.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Three Key Elements of Trade Secrets
                  </Typography>
                  <List dense>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: priColor, mr: 1 }} />
                      <ListItemText
                        primary="Information Must Have Commercial Value"
                        secondary="The information derives independent economic value from not being generally known or readily ascertainable by others."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: priColor, mr: 1 }} />
                      <ListItemText
                        primary="Information Must Be Secret"
                        secondary="The information is not generally known or easily discovered by those who might benefit from it."
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ChevronRightIcon sx={{ color: priColor, mr: 1 }} />
                      <ListItemText
                        primary="Reasonable Steps to Maintain Secrecy"
                        secondary="The owner takes reasonable measures to maintain its secrecy, such as NDAs, access controls, and security protocols."
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Common Examples in Technology
                  </Typography>
                  <List dense>
                    {[
                      "Proprietary algorithms and computational methods",
                      "Unique software architecture and engineering techniques",
                      "Database structures and compilation methodologies",
                      "Technical specifications and formulations",
                      "Specialized manufacturing processes and techniques",
                      "R&D results and experimental data",
                      "Technical know-how and specialized workflows",
                      "Business strategies and product roadmaps",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ChevronRightIcon sx={{ color: priColor, mr: 1 }} />
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <InfoBox
              title="Trade Secrets vs. Patents"
              content="Unlike patents, which require public disclosure in exchange for a limited monopoly, trade secrets rely on confidentiality. This makes them particularly valuable for protecting technical innovations that cannot be easily reverse-engineered and might otherwise have unlimited lifespan. However, they offer no protection against independent discovery or reverse engineering."
              type="primary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 2. Trade Secrets in AI */}
      <Box id="ai-trade-secrets" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${priColor}22`,
            }}
          >
            <MemoryIcon sx={{ mr: 2, color: tertColor }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: tertColor }}
            >
              Trade Secrets in Artificial Intelligence
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Artificial Intelligence presents unique trade secret opportunities
              and challenges. Many aspects of AI development, from training data
              to proprietary algorithms, qualify as trade secrets and may offer
              better protection than patents in this rapidly evolving field.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="AI Algorithms"
                  icon={<CodeIcon />}
                  content="The specific implementation details, parameter selections, and architectural designs of your AI models can be protected as trade secrets, especially when these elements give your technology a competitive advantage."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Training Methodologies"
                  icon={<LightbulbIcon />}
                  content="Unique approaches to training AI systems, including specialized data augmentation techniques, optimization strategies, and computational efficiencies in your machine learning pipeline."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Curated Datasets"
                  icon={<StorageIcon />}
                  content="Proprietary datasets with unique features, annotations, or compilations that give your AI systems superior performance or unique capabilities that competitors cannot easily reproduce."
                  color={priColor}
                />
              </Grid>
            </Grid>

            <InfoBox
              title="Why Trade Secrets Are Critical for AI Companies"
              content="In the AI industry, where innovation moves rapidly and patenting can be challenging due to subject matter eligibility issues, trade secrets often provide the most effective protection for core technological advantages. Many leading AI companies rely heavily on trade secret protection rather than patents to maintain their competitive edge."
              type="warning"
            />

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${priColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={priColor}
                fontWeight="bold"
              >
                Particular Challenges for AI Trade Secrets
              </Typography>
              <Typography variant="body2" paragraph>
                AI innovations face specific challenges that require tailored
                trade secret strategies:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Model Transparency Requirements
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Regulatory trends demanding greater AI transparency must
                        be balanced against trade secret protection, requiring
                        sophisticated legal strategies.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Collaborative Development
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        AI often involves multiple partners, open source
                        components, and third-party APIs, creating complex
                        ownership and confidentiality challenges.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Employee Mobility
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The highly competitive AI talent market means frequent
                        job changes, requiring careful protocols to prevent
                        trade secret leakage through departing employees.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Reverse Engineering Risk
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Advanced AI models may be vulnerable to model extraction
                        attacks or reverse engineering, requiring additional
                        technical and legal safeguards.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                my: 2,
                backgroundColor: "#fff3e0",
                borderLeft: "4px solid #ffb74d",
                borderRadius: 1,
              }}
            >
              <Box display="flex" alignItems="flex-start">
                <Box
                  sx={{
                    mr: 2,
                    color: "#ffb74d",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pt: 0.5,
                  }}
                >
                  <WarningIcon />
                </Box>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight="medium"
                    gutterBottom
                  >
                    Protecting Trade Secrets from AI Chatbot Risks
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", mb: 3 }}
                  >
                    Employees and vendors under NDA interacting with generative
                    AI chatbots may inadvertently expose sensitive trade
                    secrets. Key risks include:
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <StorageIcon sx={{ color: "#ffb74d", mr: 1 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            Data Storage & Security
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 4 }}>
                          A breach on servers storing user inputs can
                          inadvertently expose trade secrets. Understand the
                          implications of data location and storage security.
                        </Typography>
                        <MuiLink
                          component={RouterLink}
                          to={`/legal-terms-for-ai?term=data-location`}
                          color="primary"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textDecoration: "none",
                            mt: 1,
                            ml: 4,
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: "small",
                          }}
                        >
                          <Typography variant="body2" component="span">
                            View Data Storage Terms
                          </Typography>
                          <ChevronRightIcon fontSize="small" sx={{ ml: 0.5 }} />
                        </MuiLink>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Box display="flex" alignItems="center" mb={1}>
                          <SmartToyIcon sx={{ color: "#ffb74d", mr: 1 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            AI Model Training with User Prompts
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 4 }}>
                          AI providers may use user prompts to train and
                          fine-tune their models, embedding your confidential
                          trade secrets into AI datasets.
                        </Typography>
                        <MuiLink
                          component={RouterLink}
                          to={`/legal-terms-for-ai?term=user-data-train`}
                          color="primary"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textDecoration: "none",
                            mt: 1,
                            ml: 4,
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: "small",
                          }}
                        >
                          <Typography variant="body2" component="span">
                            View Training on User Data Terms
                          </Typography>
                          <ChevronRightIcon fontSize="small" sx={{ ml: 0.5 }} />
                        </MuiLink>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Paper>
          </CardContent>
        </Card>
      </Box>

      {/* 3. Regional Considerations - Arizona & California */}
      <Box id="regional-considerations" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${priColor}22`,
            }}
          >
            <MapIcon sx={{ mr: 2, color: tertColor }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: tertColor }}
            >
              Trade Secret Law in Arizona & California
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              While both Arizona and California have adopted versions of the
              Uniform Trade Secrets Act (UTSA), there are important
              state-specific considerations that technology companies should be
              aware of when developing their trade secret protection strategies.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    overflow: "hidden",
                    borderTop: "4px solid #5D9AB2",
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="bold"
                        sx={{ color: "#5D9AB2" }}
                      >
                        Arizona Trade Secret Law
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" paragraph>
                      Arizona follows the Uniform Trade Secrets Act with
                      relatively standard implementation. Key considerations:
                    </Typography>
                    <List dense>
                      {[
                        "Three-year statute of limitations for misappropriation claims",
                        "Courts may award attorney fees in cases of willful and malicious misappropriation",
                        "Injunctive relief available to prevent actual or threatened misappropriation",
                        "Reasonable royalties may be awarded in lieu of other damages",
                        "Courts can order appropriate measures to protect trade secrets during litigation",
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ChevronRightIcon sx={{ color: "#5D9AB2", mr: 1 }} />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    overflow: "hidden",
                    borderTop: "4px solid #E6853B",
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        fontWeight="bold"
                        sx={{ color: "#E6853B" }}
                      >
                        California Trade Secret Law
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" paragraph>
                      California has significant peculiarities in its trade
                      secret and employment laws that require special attention:
                    </Typography>
                    <List dense>
                      {[
                        "Strong restrictions on non-compete agreements (§16600 Cal. Bus. & Prof. Code)",
                        "Requirement to identify trade secrets with 'reasonable particularity' before discovery",
                        "California's 'inevitable disclosure' doctrine limitations protect employee mobility",
                        "Enhanced protection for customer lists as trade secrets under certain conditions",
                        "Special considerations for employee inventions under California Labor Code §2870-2872",
                      ].map((item, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ChevronRightIcon sx={{ color: "#E6853B", mr: 1 }} />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <InfoBox
              title="Critical Consideration: Non-Compete Restrictions in California & Federal Developments"
              content="California maintains a strong public policy favoring employee mobility, rendering most non-compete agreements void and unenforceable under state law (Cal. Bus. & Prof. Code § 16600 et seq.). Recent legislation (AB 1076 & SB 699) further solidified this ban, making it unlawful to include non-competes (with limited exceptions, like certain business sales) and providing employees with a private right of action.
Separately, in 2024, the U.S. Federal Trade Commission (FTC) issued a final rule intended to ban most new non-compete agreements nationwide and render most existing non-competes unenforceable (except for pre-existing agreements with 'senior executives'). 
While the federal landscape is uncertain given ongoing legal challenges, California's strict prohibition on non-competes remains firmly in place. Businesses operating in or hiring employees in California must continue to rely on well-drafted confidentiality agreements, non-solicitation agreements (where permissible), and robust trade secret protocols, rather than non-compete clauses, to protect their legitimate business interests."
              type="warning"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 4. Our Trade Secret Services */}
      <Box id="our-services" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${priColor}22`,
            }}
          >
            <SecurityIcon sx={{ mr: 2, color: tertColor }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: tertColor }}
            >
              Our Trade Secret Services for Technology Companies
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              We provide comprehensive trade secret protection services tailored
              specifically for technology and AI companies. Our approach
              combines legal expertise with technical understanding to develop
              effective protection strategies for your most valuable
              intellectual assets.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Trade Secret Audits & Identification"
                  icon={<SearchIcon />}
                  content="Comprehensive assessment of your technology assets to identify protectable trade secrets. We help distinguish between general knowledge and genuine trade secrets in your technical operations."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Protection Program Development"
                  icon={<LockIcon />}
                  content="Creation of comprehensive trade secret protection programs with policies, procedures, and classification schemes tailored to your specific technology and organizational structure."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Employee & Contractor Agreements"
                  icon={<DescriptionIcon />}
                  content="Crafting robust confidentiality, non-disclosure, and IP assignment agreements for employees and contractors with specific provisions for technical and AI-related confidential information."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="NDA Development & Review"
                  icon={<GavelIcon />}
                  content="Preparing and reviewing non-disclosure agreements for technology partnerships, vendor relationships, potential investors, and other third parties who may need access to your proprietary technology."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Non-Compete & NNN Agreements"
                  icon={<ScaleIcon />}
                  content="Developing enforceable non-compete and non-use, non-disclosure, non-circumvention (NNN) agreements that comply with state-specific requirements while maximizing protection of your technical assets."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Strategic IP Portfolio Planning"
                  icon={<BusinessIcon />}
                  content="Advising on optimal protection strategies, including determining which technological innovations should be protected as trade secrets versus patents, with particular focus on AI-specific considerations."
                  color={priColor}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${priColor}33`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                color={priColor}
                fontWeight="bold"
              >
                Technology-Specific Services
              </Typography>
              <Typography variant="body2" paragraph>
                Our specialized services for technology and AI companies
                include:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        AI Training Data Protection Strategies
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Developing legal frameworks to protect valuable AI
                        training datasets while ensuring compliance with data
                        privacy regulations.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Technical Data Compartmentalization
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Advising on information access controls and technical
                        compartmentalization strategies to maintain trade secret
                        status for distributed development teams.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Open Source Integration Counseling
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Guidance on integrating{" "}
                        <MuiLink
                          component={RouterLink}
                          to={`/software-licensing#foss-licenses`}
                          color="primary"
                        >
                          open source
                        </MuiLink>{" "}
                        components with proprietary software and AI systems
                        while preserving trade secret protection for your
                        innovations.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <ChevronRightIcon
                      sx={{ color: priColor, mt: 0.5, mr: 1, flexShrink: 0 }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        API Access & Technical Partner Agreements
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Structuring API licensing and technical partnership
                        agreements that provide necessary access while
                        protecting underlying trade secrets.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Patent vs. Trade Secret Strategic Counseling"
              content="One of our core services involves helping technology clients make informed decisions about whether specific innovations should be protected through patents or maintained as trade secrets. This critical decision depends on factors like enforceability, reverse engineering risk, and commercial lifespan. We provide strategic guidance tailored to your specific technology assets."
              type="primary"
            />
          </CardContent>
        </Card>
      </Box>

      {/* 5. Why Choose Us */}
      <Box id="why-choose-us" mb={4}>
        <Card sx={{ ...sectionStyles.card, mb: 2 }}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              backgroundColor: `${priColor}22`,
            }}
          >
            <CheckCircleOutlineIcon sx={{ mr: 2, color: tertColor }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: tertColor }}
            >
              Why Choose Us for Your Trade Secret Protection
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
              Our firm offers a unique combination of legal expertise and
              technological understanding that is particularly valuable for
              trade secret protection in the engineering and AI sectors. We
              understand both the legal frameworks and the technical realities
              of your innovation process.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Technical Background"
                  icon={<MemoryIcon />}
                  content="We combine legal expertise with deep understanding of engineering and technology, allowing us to better identify and protect the truly valuable technical aspects of your innovations."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="AI Specialization"
                  icon={<LightbulbIcon />}
                  content="With specific experience in artificial intelligence legal issues, we understand the unique challenges of protecting AI algorithms, training methodologies, and datasets as trade secrets."
                  color={priColor}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FeatureCard
                  title="Holistic IP Approach"
                  icon={<BusinessIcon />}
                  content="Our firm offers comprehensive intellectual property services, allowing us to develop integrated strategies that combine trade secrets with patents, trademarks, and licensing for complete protection."
                  color={priColor}
                />
              </Grid>
            </Grid>

            <Box
              sx={{ p: 3, mt: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: tertColor }}>
                Our Integrated IP Services
              </Typography>
              <Typography variant="body2" paragraph>
                By choosing our firm, you gain access to comprehensive
                intellectual property services, including:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <GavelIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        Patents
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Strategic patent protection for patentable technical
                      innovations
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/patents"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <VerifiedIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        Trademarks
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Brand protection for your technology and AI products
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/trademarks"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <DescriptionIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        AI Agreements
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Specialized agreements for AI development and licensing
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/legal-agreements-for-ai"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "white",
                      borderRadius: 1,
                      mb: 1,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <MemoryIcon
                        sx={{ color: priColor, mr: 1.5, fontSize: 22 }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        color={priColor}
                      >
                        AI Services
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="body2">
                      Comprehensive legal counsel for AI companies
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to="/ai"
                        size="small"
                        sx={{
                          backgroundColor: priColor,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: priHoverColor,
                            color: "#fff",
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <InfoBox
              title="Patent-Trade Secret Integration"
              content="Our expertise in both trade secrets and patents allows us to develop sophisticated hybrid protection strategies. We can help you identify which elements of your technology should be patented for formal protection and which proprietary details should be maintained as trade secrets for potentially perpetual protection."
              type="success"
            />
          </CardContent>
        </Card>
      </Box>

      {/* ======== CONTACT / CTA SECTION ======== */}
      <Box
        component="section"
        id="contact-section"
        sx={{
          mt: 0,
          p: 4,
          borderRadius: 2,
          backgroundColor: "#f3f6f9",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${priColor}33`,
        }}
      >
        {/* Title spanning both columns */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight="bold" color={priColor}>
            Ready to Protect Your Technical Innovations?
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{ mt: 1, fontSize: "1.1rem" }}
          >
            For Immediate Assistance, Call Us At:{" "}
            <Chip
              label="+1-602-837-0631"
              color="primary"
              sx={{
                backgroundColor: priColor,
                fontWeight: "bold",
                fontSize: "1rem",
                height: "32px",
                "& .MuiChip-label": {
                  color: "white",
                },
              }}
            />
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="flex-start">
          {/* LEFT SIDE: Image + Overview */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: priColor, mb: 2 }}
              >
                What to Expect During Your Free Trade Secret Consultation:
              </Typography>

              <List>
                {[
                  "A discussion of your technology and general identification of potential trade secret assets",
                  "Assessment of your current protection measures and potential vulnerabilities",
                  "Strategic recommendations for strengthening your trade secret program",
                  "Clear explanation of how trade secrets integrate with your broader IP strategy",
                ].map((text, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <CheckCircleOutlineIcon sx={{ color: priColor, mr: 1.5 }} />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {text}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  border: `1px solid ${priColor}33`,
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color={priColor}
                >
                  Our Trade Secret Protection Approach
                </Typography>
                <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                  We understand the unique challenges facing technology
                  companies, particularly in AI development. Our approach
                  combines robust legal protection with practical implementation
                  strategies that work within your technical environment and
                  development workflow. We focus on creating protection measures
                  that enhance security without impeding innovation.
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* RIGHT SIDE: Contact Form */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: priColor, color: "white" }}>
                <Typography variant="h5" fontWeight="medium" align="center">
                  Schedule Your Free Trade Secret Consultation
                </Typography>
              </Box>
              <CardContent>
                <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
                  Complete the form below, and our team will reach out to
                  schedule a consultation with a technology trade secret
                  attorneys.
                </Typography>

                <EmbededContactForm
                  dropDownOptions={[
                    "General Trade Secret Inquiry",
                    "NDA/Confidentiality Agreements",
                    "AI Trade Secret Protection",
                    "Trade Secret Audit & Program Development",
                    "Employee & Contractor Agreements",
                    "Technology Licensing & IP Strategy",
                    "Patent vs. Trade Secret Counseling",
                    "Other Trade Secret Matter",
                  ]}
                  submitButtonLabel="Schedule My Free Consultation"
                  disclaimerHTML="Please note that submitting this form does not create an attorney-client relationship. Any information shared will be kept confidential."
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* ======== FINAL DISCLAIMER ======== */}
      <Box
        // textAlign="center"
        mt={6}
        pt={2}
        borderTop="1px solid #ccc"
        sx={{
          backgroundColor: "#f9f9f9",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          Disclaimer: Please do not share confidential details until we confirm
          our representation. This website provides general information and is
          not intended as legal advice for your specific situation.
        </Typography>
      </Box>
    </Container>
  );
};

export default KthTradeSecrets;
